import type { ReactNode } from 'react';
import { contactsModule } from './contacts';
import { calendarModule } from './calendar';
import { hrModule } from './hr';
import { offersModule } from './offers';
import { productsModule } from './products';
import { ordersModule } from './orders';
import { shiftsModule } from './shifts';
import { chartModule, financeModule, purchaseModule } from './finance';
import { documentsModule } from './documents';
import { inventoryModule } from './inventory';
import { fleetModule } from './fleet';
import { financingModule } from './financing';
import { ridebookModule } from './ridebook';
import { contractsModule } from './contracts';
import { payrollModule } from './payroll';
import { vouchersModule } from './vouchers';
import { reportsModule } from './reports';
import { marketingModule } from './marketing';
import { importsModule } from './imports';
import { crmModule } from './crm';
import { webchatModule } from './webchat';
import type { SetupSection } from '../setupSections';

/**
 * Beschreibt, was ein Fachmodul dem Backoffice mitbringt: seinen Schlüssel aus packages/shared (modules.ts), den Pfad in der
 * Seitenleiste und seine Routen. Neue Module tragen sich unten ein. Sonst ändert sich an main.tsx und AppShell.tsx nichts.
 *
 * Die älteren Seiten (Anfragen, Benutzer, Einrichtung) liegen noch in src/pages und sind dort fest verdrahtet.
 */
export interface NavEntry {
  label: string;
  path: string;
  /** Recht, das der Benutzer haben muss, damit der Eintrag erscheint (zum Beispiel `contacts.finance` für Debitoren und Kreditoren) */
  permission?: string;
  /** Gruppe der Seitenleiste, wenn der Eintrag nicht in der Gruppe seines Moduls stehen soll (siehe navigation.ts) */
  group?: string;
  /** Schlüssel eines Moduls der Zielgruppe: Der Eintrag steht direkt hinter dessen Einträgen statt am Ende der Gruppe (Debitoren und Kreditoren folgen den Dokumenten) */
  after?: string;
  /** Pfade von Einträgen derselben Gruppe: Der Eintrag steht direkt hinter dem ersten davon, der sichtbar ist (Finanzreport hinter dem Anlagenspiegel). Fehlt jeder, bleibt er an seinem Platz. */
  afterPath?: string[];
}

export interface BackofficeModule {
  key: string;
  /** Pfad des Moduls in der Seitenleiste. Hat das Modul mehrere Einträge (`nav`), dient er nur als Vorgabe. */
  path: string;
  routes: ReactNode;
  /** Mehrere Einträge in der Seitenleiste statt eines einzigen, zum Beispiel „Aufgaben“ und „Kalender & Termine“ */
  nav?: NavEntry[];
  /** Bereiche, die das Modul zur Einrichtung beisteuert (Reiter hinter dem Zahnrad), und ihre Routen unter /einrichtung */
  setupSections?: SetupSection[];
  setupRoutes?: ReactNode;
}

export const backofficeModules: BackofficeModule[] = [contactsModule, offersModule, ordersModule, productsModule, calendarModule, hrModule, shiftsModule, financeModule, purchaseModule, chartModule, financingModule, documentsModule, inventoryModule, fleetModule, ridebookModule, contractsModule, payrollModule, vouchersModule, reportsModule, marketingModule, importsModule, crmModule, webchatModule];

/** Pfade in der Seitenleiste je Modulschlüssel */
export const modulePaths: Record<string, string> = Object.fromEntries(backofficeModules.map((m) => [m.key, m.path]));

/** Module mit mehreren Einträgen in der Seitenleiste */
export const moduleNav: Record<string, NavEntry[]> = Object.fromEntries(backofficeModules.filter((m) => m.nav).map((m) => [m.key, m.nav!]));
